import { useNavigate } from 'react-router-dom';
import { Calculator, FileText, MapPin, Calendar } from 'lucide-react';
import { WorkerProject } from '../types/worker.types';
import { WorkerLayout } from '../components/layout/WorkerLayout';
import { Card, CardContent, Button } from '../components/ui';

interface WorkerHomeProps {
  project: WorkerProject;
  token: string;
}

export function WorkerHome({ project, token }: WorkerHomeProps) {
  const navigate = useNavigate();

  const handleNavigateToDocuments = () => {
    navigate(`/worker/${token}/documents`);
  };

  const handleNavigateToCalculator = () => {
    navigate(`/worker/${token}/calculator`);
  };


  return (
    <WorkerLayout project={project}>
      <div className="space-y-6">
        {/* Message de bienvenue */}
        {project.config.branding.custom_welcome_message && (
          <Card>
            <CardContent>
              <p className="text-gray-700 text-center">
                {project.config.branding.custom_welcome_message}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Informations du projet */}
        <Card>
          <CardContent className="space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {project.name}
              </h2>
              {project.description && (
                <p className="text-gray-600 mb-3">
                  {project.description}
                </p>
              )}
            </div>

            {project.address && (
              <div className="flex items-center space-x-2 text-gray-600">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{project.address}</span>
              </div>
            )}

            <div className="flex items-center space-x-2 text-gray-600">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">
                Accès disponible 24h/24
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Modules disponibles */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Modules disponibles
          </h3>

          <div className="grid gap-4">
            {project.config.modules.documents.enabled && (
              <Card hover>
                <CardContent>
                  <Button
                    onClick={handleNavigateToDocuments}
                    variant="ghost"
                    fullWidth
                    className="h-auto p-4 justify-start"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-medium text-gray-900">
                          Documents
                        </h4>
                        <p className="text-sm text-gray-500">
                          Consulter les documents du chantier
                        </p>
                      </div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            )}

            {project.config.modules.calculator.enabled && (
              <Card hover>
                <CardContent>
                  <Button
                    onClick={handleNavigateToCalculator}
                    variant="ghost"
                    fullWidth
                    className="h-auto p-4 justify-start"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                        <Calculator className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="text-left">
                        <h4 className="font-medium text-gray-900">
                          Calculatrice
                        </h4>
                        <p className="text-sm text-gray-500">
                          Outils de calcul pour le chantier
                        </p>
                      </div>
                    </div>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {!project.config.modules.documents.enabled && !project.config.modules.calculator.enabled && (
            <Card>
              <CardContent>
                <p className="text-center text-gray-500">
                  Aucun module n'est activé pour ce projet.
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Informations techniques */}
        <Card>
          <CardContent>
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-500">
                Projet géré par {project.company?.name}
              </p>
              <div className="flex items-center justify-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-green-500"></div>
                <span className="text-xs text-gray-400">
                  Connexion sécurisée
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </WorkerLayout>
  );
}